/**
 * Made.bb Application Root
 * Handles routing between public and protected pages
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Gone } from './components/Gone';
import { ComingSoon } from './components/ComingSoon';
import LandingPage from './components/LandingPage';
import { MadebbLogin } from './components/MadebbLogin';
import { Calculator } from './components/Calculator';
import { MadebbProtectedRoute } from './components/MadebbProtectedRoute';
import { Inventory } from './components/Inventory';
import InventoryCountEntry from './components/InventoryCountEntry';
import InventoryCountSheet from './components/InventoryCountSheet';
import { ManufacturingHub } from './components/ManufacturingHub';
import { MadebbLayout } from './components/MadebbLayout';
import { OrderStatusUpdate } from './components/OrderStatusUpdate';
import { ShopAdminPage } from './pages/ShopAdminPage';
import { EReceiptsPage } from './pages/EReceiptsPage';
import { StaffDashboard } from './pages/StaffDashboard';
import { PrinterManagementPage } from './pages/PrinterManagementPage';
import CountSessionStart from './components/CountSessionStart';
import { InvoiceListPage } from './pages/InvoiceListPage';
import { InvoiceEditorPage } from './pages/InvoiceEditorPage';
import { PublicInvoicePaymentPage } from './pages/PublicInvoicePaymentPage';

// MADE-255: Feature flag — read at build time from Vite env.
// Set VITE_FEATURE_NEW_LANDING=true on Railway staging to enable Epic 3 landing page.
// Production default: false (ComingSoon renders).
const newLandingEnabled = import.meta.env.VITE_FEATURE_NEW_LANDING === 'true';

export const MadebbApp: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      {/* MADE-255: Feature-flagged root — LandingPage (Epic 3) or ComingSoon fallback */}
      <Route path="/" element={newLandingEnabled ? <LandingPage /> : <ComingSoon />} />
      <Route path="/login" element={<MadebbLogin />} />
      <Route path="/status/:orderId" element={<OrderStatusUpdate />} />
      <Route path="/invoice-pay/:token" element={<PublicInvoicePaymentPage />} />
      {/* MADE-290: PWA home-screen shortcut — resolves active session, redirects to /count/:sessionId */}
      <Route path="/count/start" element={<CountSessionStart />} />
      <Route path="/count/:sessionId" element={<InventoryCountEntry />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <MadebbProtectedRoute>
            <StaffDashboard />
          </MadebbProtectedRoute>
        }
      />
      <Route
        path="/roll-shade-order"
        element={
          <MadebbProtectedRoute>
            <MadebbLayout title="Roll Shade Calculator">
              <Calculator />
            </MadebbLayout>
          </MadebbProtectedRoute>
        }
      />

      {/* MADE-065/071: /orders deprecated — redirect to /manufacturing */}
      <Route path="/orders" element={<Navigate to="/manufacturing" replace />} />

      <Route
        path="/inventory"
        element={
          <MadebbProtectedRoute>
            <MadebbLayout title="Inventory">
              <Inventory />
            </MadebbLayout>
          </MadebbProtectedRoute>
        }
      />

      {/* MADE-065/071: /production deprecated — redirect to /manufacturing */}
      <Route path="/production" element={<Navigate to="/manufacturing" replace />} />

      <Route
        path="/count-sheet"
        element={
          <MadebbProtectedRoute>
            <InventoryCountSheet />
          </MadebbProtectedRoute>
        }
      />

      {/* MADE-018: ManufacturingHub — staff-only gate */}
      <Route
        path="/manufacturing"
        element={
          <MadebbProtectedRoute requireStaff>
            <MadebbLayout title="Production">
              <ManufacturingHub />
            </MadebbLayout>
          </MadebbProtectedRoute>
        }
      />

      <Route
        path="/shop-admin"
        element={
          <MadebbProtectedRoute>
            <MadebbLayout title="Shop Admin">
              <ShopAdminPage />
            </MadebbLayout>
          </MadebbProtectedRoute>
        }
      />

      <Route
        path="/invoices"
        element={<MadebbProtectedRoute requireStaff><MadebbLayout title="Invoices"><InvoiceListPage /></MadebbLayout></MadebbProtectedRoute>}
      />
      <Route
        path="/invoices/new"
        element={<MadebbProtectedRoute requireStaff><MadebbLayout title="New invoice"><InvoiceEditorPage /></MadebbLayout></MadebbProtectedRoute>}
      />
      <Route
        path="/invoices/:id"
        element={<MadebbProtectedRoute requireStaff><MadebbLayout title="Invoice"><InvoiceEditorPage /></MadebbLayout></MadebbProtectedRoute>}
      />

      <Route
        path="/shop-admin/ereceipts"
        element={
          <MadebbProtectedRoute>
            <MadebbLayout title="eReceipts">
              <EReceiptsPage />
            </MadebbLayout>
          </MadebbProtectedRoute>
        }
      />

      {/* MADE-275: Printer Management */}
      <Route
        path="/printers"
        element={
          <MadebbProtectedRoute>
            <MadebbLayout title="Manage Printers">
              <PrinterManagementPage />
            </MadebbLayout>
          </MadebbProtectedRoute>
        }
      />

      {/* Redirect any unknown routes to landing */}
      <Route path="*" element={<Navigate to="/madebb" replace />} />
    </Routes>
  );
};
